import { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";
import { useLanguage } from "@/contexts/LanguageContext";

function Counter({ target, suffix = "", prefix = "" }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = Date.now();
    const timer = setInterval(() => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * target));
      if (p >= 1) { setCount(target); clearInterval(timer); }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

const photos = [
  { src: "/assets/fotos/hb-foto-k3.webp",  label: "Kohlbrenner Lodges" },
  { src: "/assets/fotos/hb-foto-3.webp",    label: "Holztragwerk" },
  { src: "/assets/fotos/hb-foto-1-1.webp",  label: "Holzfassade" },
  { src: "/assets/fotos/hb-foto-7.webp",    label: "Terrasse Südtirol" },
];

export function StatsSection() {
  const { lang } = useLanguage();

  const stats = lang === "it"
    ? [
        { target: 62,  suffix: "+", label: "Anni di esperienza",  note: "Dal 1962 in Alto Adige" },
        { target: 500, suffix: "+", label: "Progetti realizzati", note: "Privato · Hotel · Commerciale" },
        { target: 3,   suffix: ".", label: "Generazioni",         note: "Hafner EC GmbH" },
        { target: 100, suffix: "%", label: "Alto Adige",          note: "Pianificazione & Esecuzione" },
      ]
    : [
        { target: 62,  suffix: "+", label: "Jahre Erfahrung",     note: "Seit 1962 in Südtirol" },
        { target: 500, suffix: "+", label: "Projekte realisiert", note: "Privat · Hotel · Gewerbe" },
        { target: 3,   suffix: ".", label: "Generationen",        note: "Hafner EC GmbH" },
        { target: 100, suffix: "%", label: "Südtirol",            note: "Planung & Ausführung" },
      ];

  return (
    <section className="overflow-hidden" style={{ background: "#0b0c0e" }} data-testid="stats-section">
      {/* Stats numbers row */}
      <div className="container-hb py-16 md:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-0 lg:divide-x divide-white/10">
          {stats.map((stat, i) => (
            <div key={i} className="lg:px-10 first:pl-0 last:pr-0">
              <div
                className="font-bebas text-white leading-none mb-2"
                style={{ fontSize: "clamp(56px, 7vw, 88px)", color: i === 0 || i === 1 ? "#f5c800" : "#ffffff" }}
              >
                <Counter target={stat.target} suffix={stat.suffix} />
              </div>
              <div className="font-manrope font-bold text-white/90 text-sm mb-0.5">{stat.label}</div>
              <div className="text-xs font-inter" style={{ color: "rgba(255,255,255,0.35)" }}>{stat.note}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Photo strip */}
      <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: "2px" }}>
        {photos.map((photo, i) => (
          <div
            key={i}
            className="relative overflow-hidden group"
            style={{ height: "clamp(180px, 22vw, 300px)" }}
          >
            <img
              src={photo.src}
              alt={photo.label}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              style={{ objectPosition: "center" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p
                className="text-[10px] tracking-widest uppercase font-inter font-semibold"
                style={{ color: "rgba(245,200,0,0.85)" }}
              >
                {photo.label}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
